import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { referralService } from '@/services/referralService';
import type {
  ReferralFilters,
  ReferralUserFilters,
  AdjustCreditsInput,
  ChangeTierInput,
  RejectReferralInput,
} from '@/types/referral.types';

// Stats
export function useReferralStats() {
  return useQuery({
    queryKey: ['admin', 'referrals', 'stats'],
    queryFn: referralService.getStats,
    staleTime: 1000 * 30, // 30 seconds
  });
}

// Referrals list
export function useReferrals(filters?: ReferralFilters) {
  return useQuery({
    queryKey: ['admin', 'referrals', 'list', filters],
    queryFn: () => referralService.getReferrals(filters),
    staleTime: 1000 * 30,
  });
}

// Single referral
export function useReferral(id: string) {
  return useQuery({
    queryKey: ['admin', 'referrals', id],
    queryFn: () => referralService.getReferral(id),
    enabled: !!id,
    staleTime: 1000 * 30,
  });
}

// Validate referral
export function useValidateReferral() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => referralService.validateReferral(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// Reject referral
export function useRejectReferral() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RejectReferralInput }) =>
      referralService.rejectReferral(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// Validate all pending
export function useValidateAllReferrals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: referralService.validateAllPending,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// Users list
export function useReferralUsers(filters?: ReferralUserFilters) {
  return useQuery({
    queryKey: ['admin', 'referrals', 'users', filters],
    queryFn: () => referralService.getUsers(filters),
    staleTime: 1000 * 30,
  });
}

// Single user
export function useReferralUser(userId: string) {
  return useQuery({
    queryKey: ['admin', 'referrals', 'users', userId],
    queryFn: () => referralService.getUser(userId),
    enabled: !!userId,
    staleTime: 1000 * 30,
  });
}

// Adjust credits
export function useAdjustCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: AdjustCreditsInput }) =>
      referralService.adjustCredits(userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// Change tier
export function useChangeTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: ChangeTierInput }) =>
      referralService.changeTier(userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// Rewards
export function useReferralRewards() {
  return useQuery({
    queryKey: ['admin', 'referrals', 'rewards'],
    queryFn: referralService.getRewards,
    staleTime: 1000 * 60,
  });
}
